
import React, { useState } from 'react';
import {
  Users, Briefcase, Search, Target, Zap, PenTool,
  PlayCircle, Filter, CheckCircle, Mail, Calendar,
  FileText, TrendingUp, UserCheck, GitMerge, LayoutTemplate,
  Share2, BarChart3, MessageSquare, Phone, ClipboardList, Send, Star, ShieldCheck, ClipboardCheck, Globe
} from 'lucide-react';
import { VacancyStep, Candidate, Vacancy, Responsibility, SubscriptionPlan, TeamMember, SupportRequest, RecruiterTask } from './types';

/** Standaard tenant (Werfplaats): `client_contacts.client_id` moet hierop wijzen; CRM-bedrijf staat in `linked_client_id`. */
export const WERFTOOLS_DEFAULT_TENANT_CLIENT_ID = '62288053-c24c-4bfe-8ae9-b6b239b12469';

/** Logo in klantportaal (login + navigatie). */
export const CLIENT_PORTAL_LOGO_URL =
  'https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/website/logo%20werfplaats%20(2000%20x%20400%20px).png';

export const WERFTOOLS_LOGO_URL =
  'https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/website/Werftools.png';

// Define standard recruitment stages in Dutch
export const DEFAULT_KANBAN_STAGES: { id: string, name: string, type: 'Screening' | 'Interview' | 'Offer' | 'Hired' | 'Rejected', sortOrder: number }[] = [
  { id: 'Afgewezen', name: 'Afgewezen', type: 'Rejected', sortOrder: 0 },
  { id: 'Shortlist', name: 'Shortlist', type: 'Screening', sortOrder: 1 },
  { id: 'Voorgesteld', name: 'Voorgesteld', type: 'Screening', sortOrder: 2 },
  { id: 'Gesprek', name: 'Gesprek', type: 'Interview', sortOrder: 3 },
  { id: 'Aanbod', name: 'Aanbod', type: 'Offer', sortOrder: 4 },
  { id: 'Plaatsing', name: 'Plaatsing', type: 'Hired', sortOrder: 5 }
];

// Translated Steps to Dutch - Consolidate Operational Steps into Interview Flow
export const STEPS = [
  { id: VacancyStep.INTAKE, icon: <Briefcase size={18} />, label: '1. Intake' },
  { id: VacancyStep.RESEARCH, icon: <Search size={18} />, label: '2. Onderzoek & Persona' },
  { id: VacancyStep.COMM_TEMPLATES, icon: <LayoutTemplate size={18} />, label: '3. Vacature Builder' },
  { id: VacancyStep.PUT_LIVE, icon: <Globe size={18} />, label: '4. Publiceren Werken-bij' },
  { id: VacancyStep.STRATEGY, icon: <Zap size={18} />, label: '5. Campagne' },
  { id: VacancyStep.SOURCING, icon: <Globe size={18} />, label: '6. AI Sourcing' },

  // Marketing Phase (Refactored)
  // The steps PREMIUM_JOB_BOARDS, SHARE_NETWORK, SCREEN_TALENT_POOL, OPTIMIZATION, SHORTLIST, INTERVIEW_KANBAN
  // have been fully migrated to the new VacancyDashboard.tsx tabs!
];

// Translated Plans to Dutch
export const PLANS: SubscriptionPlan[] = [
  {
    id: 'solo',
    name: 'Single Vacancy',
    price: '€1.250 /vacature',
    features: ['AI Marktonderzoek & Persona', 'Outreach 1:1', 'Basis Kanban Bord'],
    vacancyLimit: 1
  },
  {
    id: 'hybrid',
    name: 'Hybrid Growth',
    price: '€2.499 /mnd',
    features: ['Onbeperkte AI Tools', 'Werkplaats Recruiter Support', 'Team Management'],
    vacancyLimit: 5
  },
  {
    id: 'scale',
    name: 'Enterprise Flow',
    price: 'Op aanvraag',
    features: ['Volledige Automatisering', 'Maatwerk Integraties', 'Dedicated Account Manager'],
    vacancyLimit: 999
  }
];

export const MOCK_TEAM: TeamMember[] = [
  { id: '00000000-0000-0000-0000-000000000101', name: 'Linda Veenstra', role: 'Internal Recruiter', email: 'linda@fleetconnect.nl', joinedAt: '2023-11-12' },
  { id: '00000000-0000-0000-0000-000000000102', name: 'Sven de Groot', role: 'Hiring Manager', email: 'sven@fleetconnect.nl', joinedAt: '2024-01-05' }
];

export const MOCK_CLIENTS = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    name: 'TechNova Solutions',
    industry: 'IT',
    contact_person: 'Mark de Vries',
    email: 'mark@technova.nl',
    activeVacancies: 3,
    logo_url: 'https://api.dicebear.com/7.x/initials/svg?seed=TN',
    subscription: 'Hybrid Growth',
    address_street: 'Science Park 402',
    address_city: 'Amsterdam',
    address_zip: '1098 XH',
    client_type: 'Direct'
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    name: 'GreenBuild',
    industry: 'Construction',
    contact_person: 'Sarah Jansen',
    email: 's.jansen@greenbuild.nl',
    activeVacancies: 1,
    logo_url: 'https://api.dicebear.com/7.x/initials/svg?seed=GB',
    address_street: 'Bouwstraat 12',
    address_city: 'Rotterdam',
    address_zip: '3011 AA',
    client_type: 'Direct'
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    name: 'HealthDirect',
    industry: 'Healthcare',
    contact_person: 'Pieter Berg',
    email: 'p.berg@healthdirect.nl',
    activeVacancies: 5,
    logo_url: 'https://api.dicebear.com/7.x/initials/svg?seed=HD',
    address_street: 'Zorglaan 8',
    address_city: 'Utrecht',
    address_zip: '3584 CS',
    client_type: 'Direct'
  }
];

export const MOCK_VACANCIES: Vacancy[] = [
  {
    id: '00000000-0000-0000-0000-000000000201',
    title: 'Senior Frontend Engineer',
    clientId: '00000000-0000-0000-0000-000000000001',
    status: VacancyStep.PUT_LIVE,
    createdAt: '2024-03-01',
    updatedAt: '2024-03-01',
    description: 'Lead modern UI development.',
    responsibilities: {
      [VacancyStep.INTERVIEW_KANBAN]: Responsibility.SHARED,
    },
    data: {
      isVerifiedByRecruiter: true,
      marketResearchSummary: 'Hoge schaarste voor React/TypeScript experts in de randstad.'
    }
  },
  {
    id: '00000000-0000-0000-0000-000000000202',
    title: 'Machine Learning Specialist',
    clientId: '00000000-0000-0000-0000-000000000002',
    status: VacancyStep.RESEARCH,
    createdAt: '2024-03-10',
    updatedAt: '2024-03-10',
    description: 'AI R&D focus.',
    responsibilities: {},
    data: { assignedRecruiterId: undefined }
  },
];

export const MOCK_SUPPORT_REQUESTS: SupportRequest[] = [
  { id: 'req-1', clientId: '00000000-0000-0000-0000-000000000001', clientName: 'TechNova Solutions', subject: 'Vraag over persona', message: 'Hoi! Ik zie dat de persona erg gefocust is op Randstad, kunnen we dit verbreden naar heel NL?', timestamp: '2024-03-15 10:15', status: 'Pending', priority: 'Medium' },
  { id: 'req-2', clientId: '00000000-0000-0000-0000-000000000003', clientName: 'HealthDirect', subject: 'Probleem met outreach', message: 'De LinkedIn templates lijken niet helemaal aan te slaan bij artsen. Hebben jullie tips?', timestamp: '2024-03-15 11:30', status: 'Pending', priority: 'High' }
];

export const MOCK_RECRUITER_TASKS: RecruiterTask[] = [
  { id: 'task-1', title: 'Screening Jasper v.d. Berg', deadline: 'Vandaag', completed: false, priority: 'High' },
  { id: 'task-2', title: 'Intake meeting GreenBuild', deadline: 'Morgen', completed: false, priority: 'Medium' },
  { id: 'task-3', title: 'Contract check Sophie de Jong', deadline: 'Gisteren', completed: true, priority: 'High' }
];

export const MOCK_CANDIDATES: Candidate[] = [
  { id: '00000000-0000-0000-0000-000000000301', name: 'Jasper van den Berg', email: 'jasper@example.com', role: 'Frontend Developer', experience: 5, status: 'Available', tags: ['React', 'TypeScript'], addedAt: '2024-03-01' },
  { id: '00000000-0000-0000-0000-000000000302', name: 'Sophie de Jong', email: 'sophie@example.com', role: 'UX Designer', experience: 3, status: 'In Process', tags: ['Figma', 'UI/UX'], addedAt: '2024-03-05' },
  { id: '00000000-0000-0000-0000-000000000303', name: 'Bram Bakker', email: 'bram@example.com', role: 'Project Manager', experience: 8, status: 'Placed', tags: ['Agile', 'Jira'], addedAt: '2024-02-15' },
  { id: '00000000-0000-0000-0000-000000000304', name: 'Eva Visser', email: 'eva@example.com', role: 'Fullstack Dev', experience: 4, status: 'Available', tags: ['Node.js', 'PostgreSQL'], addedAt: '2024-03-10' },
  { id: '00000000-0000-0000-0000-000000000305', name: 'Liam Smit', email: 'liam@example.com', role: 'Backend Dev', experience: 6, status: 'Available', tags: ['Go', 'Cloud'], addedAt: '2024-03-12' },
];
